/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { FC } from 'react';
import {
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Slide,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
} from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import colors from '../../themes/main-colors';
import useContentMgmt, {
  PageTextContentType,
} from '../../hooks/useContentMgmt';
import { TransitionProps } from '@material-ui/core/transitions';
import microcopyGroupIds from './microcopyGroupIds';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children?: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type Props = {
  open: boolean;
  onClose: () => void;
  onSaveAndExit: () => void;
};

const SaveAndComeBackModal: FC<Props> = ({
  open,
  onClose,
  onSaveAndExit,
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const { content } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_SAVE_AND_COME_BACK_LATER,
  );

  return (
    <Dialog
      open={open}
      TransitionComponent={isMobile ? Transition : undefined}
      onClose={onClose}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      scroll="body"
      fullWidth={true}
      disableRestoreFocus={true}
      PaperProps={{
        style: {
          maxWidth: '530px',
          margin: 0,
        },
      }}
    >
      <Paper
        css={{
          [theme.breakpoints.down('xs')]: {
            maxWidth: '600px !important',
            width: '100%',
            position: 'fixed',
            bottom: 0,
            left: 0,
          },
        }}
      >
        <DialogTitle css={{ padding: '24px 24px 0' }}>
          <Grid
            container
            css={{
              justifyContent: 'end',
            }}
          >
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={onClose}
              css={{
                padding: 0,
              }}
            >
              <Close />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent
          css={{
            [theme.breakpoints.down('xs')]: {
              padding: '0 24px 40px',
            },
          }}
        >
          <Grid container>
            <Grid item>
              <Typography variant={'h1'} css={{ marginBottom: '20px' }}>
                {content.get('SAVE_COME_BACK_LATER_HEADER')}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant={'body1'}>
                {content.get('SAVE_COME_BACK_LATER_DETAILS')}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          css={{
            padding: '16px 24px 50px',
            [theme.breakpoints.down('xs')]: {
              backgroundColor: colors.cloudGrey,
              padding: '30px 0',
              boxShadow: `0px -3px 6px ${colors.boxShadowColor}`,
              opacity: 1,
            },
          }}
        >
          <Grid
            container
            css={{
              justifyContent: 'center',
              columnGap: '20px',
            }}
          >
            <Grid
              item
              xs={5}
              md={5}
              css={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                type="button"
                fullWidth
                variant="outlined"
                onClick={onClose}
                css={{
                  borderRadius: '30px',
                  width: '93%',
                  borderColor: colors.sparkBlue,
                  color: colors.sparkBlue,
                  height: '48px',
                  fontSize: '18px',
                  [theme.breakpoints.down('sm')]: {
                    width: '100%',
                    fontSize: '16px',
                  },
                }}
              >
                {content.get('REUSABLE_BUTTON_BACK')}
              </Button>
            </Grid>
            <Grid item xs={5} md={5}>
              <Button
                type="submit"
                color={'primary'}
                fullWidth
                variant="contained"
                css={{
                  borderRadius: '30px',
                  width: '93%',
                  height: '48px',
                  fontSize: '18px',
                  backgroundColor: colors.orange,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: colors.orange,
                    boxShadow: 'none',
                  },
                  [theme.breakpoints.down('sm')]: {
                    width: '100%',
                    fontSize: '16px',
                  },
                }}
                onClick={onSaveAndExit}
              >
                {content.get('REUSABLE_SAVE_AND_EXIT_BUTTON')}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Paper>
    </Dialog>
  );
};

export default SaveAndComeBackModal;
